import React, { useEffect, useState, useRef } from "react";
import { RadioButton, Tooltip } from "@vibe/core";
import SettingsDropdown from "../../SettingsDropdown";

interface UpdatesSettingsProps {
  boardSettingsData: any; // TODO: Add proper type
  onUpdatePreferenceChange: (preference: string, excludedBoardIds?: string[]) => void;
  options: { value: string; label: string }[];
}

// Dummy board options

const UpdatesSettings = ({ boardSettingsData, onUpdatePreferenceChange, options }: UpdatesSettingsProps) => {
  const [selectedUpdatePreference, setSelectedUpdatePreference] = useState("");
  const [selectedBoards, setSelectedBoards] = useState<{ label: string; value: string | number }[]>([]);
  const previousBoardsRef = useRef<{ label: string; value: string | number }[]>([]);

  useEffect(() => {
    if (boardSettingsData?.updates?.preference) {
      setSelectedUpdatePreference(boardSettingsData?.updates?.preference);
    }

    if (boardSettingsData?.updates?.excluded_board_ids && options) {
      const boards = options.filter((option) => boardSettingsData?.updates?.excluded_board_ids?.includes(option.value));
      if (JSON.stringify(boards) !== JSON.stringify(previousBoardsRef.current)) {
        setSelectedBoards(boards);
        previousBoardsRef.current = boards;
      }
    }
  }, [boardSettingsData, options]);

  const handleRadioButtonSelect = (event: React.ChangeEvent<HTMLInputElement | null>) => {
    if (event.target) {
      setSelectedUpdatePreference(event.target.value);
      setSelectedBoards([]); // Clear selected boards when radio button is selected
      onUpdatePreferenceChange(event.target.value, []);
    }
  };

  const handleDropdownChange = (selectedOptions: any) => {
    if (!selectedOptions) {
      setSelectedBoards([]);
      setSelectedUpdatePreference("sync_to_all");
      onUpdatePreferenceChange("sync_to_all", []);
      return;
    }

    setSelectedBoards(selectedOptions);

    if (selectedOptions.length > 0) {
      setSelectedUpdatePreference(""); // Deselect radio buttons
      onUpdatePreferenceChange(
        "sync_to_all_except",
        selectedOptions.map((option: any) => option.value)
      );
    } else {
      // If no boards are selected, default to sync_all_updates
      setSelectedUpdatePreference("sync_to_all");
      onUpdatePreferenceChange("sync_to_all", []);
    }
  };

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Tooltip content="This will enable updates for all shared items in this board. You can override this setting for specific items in the item settings.">
          <RadioButton
            checked={selectedUpdatePreference === "sync_to_all"}
            text="When updates are posted on shared items in this board,
            sync them to all other boards"
            name="board-updates-radio-button"
            onSelect={(event) => handleRadioButtonSelect(event)}
            value="sync_to_all"
          />
        </Tooltip>
        <Tooltip content="This will disable updates for all shared items in this board. You can override this setting for specific items in the item settings.">
          <RadioButton
            checked={selectedUpdatePreference === "sync_to_none"}
            text="Never add updates from this board to other boards"
            name="board-updates-radio-button"
            onSelect={(event) => handleRadioButtonSelect(event)}
            value="sync_to_none"
          />
        </Tooltip>
      </div>
      <SettingsDropdown
        label="Sync updates to all linked boards except:"
        placeholder="Select Board"
        options={options}
        onChange={handleDropdownChange}
        multi={true}
        defaultValue={selectedBoards}
      />
    </div>
  );
};

export default UpdatesSettings;
